import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { getAnalyses, createPortalSession } from '../utils/api';

const Account = () => {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalyses();
  }, []);

  const loadAnalyses = async () => {
    try {
      const response = await getAnalyses();
      setAnalyses(response.data.analyses || []);
    } catch (err) {
      console.error('Failed to load analyses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await createPortalSession();
      window.location.href = response.data.url;
    } catch (err) {
      alert('Failed to open subscription management. Please try again or contact support.');
    }
  };

  const getRiskColor = (risk) => {
    const riskLower = risk?.toLowerCase() || 'moderate';
    if (riskLower === 'low') return 'text-green-600 bg-green-100';
    if (riskLower === 'moderate') return 'text-yellow-600 bg-yellow-100';
    if (riskLower === 'high') return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8">Account Dashboard</h1>

      {/* Account Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Current Plan</div>
          <div className="text-2xl font-bold capitalize">{user?.tier || 'Free'}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Scans Remaining</div>
          <div className="text-2xl font-bold">
            {user?.tier === 'enterprise' ? '∞' : user?.scans_remaining || 0}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">This Month</div>
          <div className="text-2xl font-bold">{user?.scans_this_month || 0}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-1">Total Lifetime</div>
          <div className="text-2xl font-bold">{user?.total_scans_lifetime || 0}</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold mb-2">Ready to analyse another contract?</h2>
            <p className="text-gray-600">Upload a new contract to get instant AI analysis</p>
          </div>
          <div className="flex gap-4">
            <Link
              to="/upload"
              className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-3 rounded-lg font-semibold"
            >
              Upload Contract
            </Link>
            <Link
              to="/pricing"
              className="bg-white text-primary-600 hover:bg-gray-50 border border-primary-600 px-6 py-3 rounded-lg font-semibold"
            >
              Upgrade Plan
            </Link>
          </div>
        </div>
      </div>

      {/* Analysis History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold">Analysis History</h2>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : analyses.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p>No analyses yet. Upload your first contract to get started!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {analyses.map((analysis) => (
              <Link
                key={analysis.id}
                to={`/results/${analysis.id}`}
                className="block p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">{analysis.contract_name}</h3>
                    <div className="flex gap-4 text-sm text-gray-600 mb-2">
                      <span>Type: {analysis.contract_type}</span>
                      <span>Country: {analysis.contract_country}</span>
                      <span>Pages: {analysis.page_count}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(analysis.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      {analysis.risk_score?.toFixed(1) || 'N/A'}
                    </div>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getRiskColor(
                        analysis.overall_risk
                      )}`}
                    >
                      {analysis.overall_risk?.toUpperCase() || 'MODERATE'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold">Account Information</h2>
          {user?.tier && ['monthly', 'pro', 'enterprise', 'unlimited'].includes(user.tier) && (
            <button
              onClick={handleManageSubscription}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium text-sm"
            >
              Manage Subscription
            </button>
          )}
        </div>
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-semibold">Email:</span> {user?.email}
          </div>
          <div>
            <span className="font-semibold">Member since:</span>{' '}
            {new Date(user?.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
